import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Heart, LogOut, Package } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { announcement } from "../mock/mock";
import { fetchCategories } from "../lib/api";
import { LOGO_URL } from "../lib/brand";

const Header = () => {
  const { count } = useCart();
  const { user, signOut } = useAuth();
  const { count: wishCount } = useWishlist();
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate("/shop");
  };

  const doLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-[#0e2a1e] text-[#f4f2ea] text-center text-[13px] tracking-wide py-2.5 px-4">
        {announcement}
      </div>

      <div className="bg-[#f4f2ea] border-b border-[#0e2a1e]/10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Everyone's Budget" className="w-11 h-11 rounded-full object-cover" />
            <span className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] hidden sm:block">
              Everyone's Budget
            </span>
          </Link>

          <form onSubmit={handleSearch} className="flex-1 max-w-2xl mx-auto flex">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clothing, home, categories.."
              className="flex-1 bg-white border border-[#0e2a1e]/15 rounded-l-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40 transition-colors"
            />
            <button
              type="submit"
              className="bg-[#0e2a1e] text-[#f4f2ea] px-5 rounded-r-md text-sm font-medium hover:bg-[#164031] transition-colors"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-5 shrink-0">
            <Link to="/account" className="hidden md:flex items-center gap-1.5 text-[#0e2a1e] hover:opacity-70 transition-opacity relative" title="Wishlist">
              <Heart size={20} />
              {user && wishCount > 0 && (
                <span className="absolute -top-2 -left-2 bg-[#a4d61e] text-[#0e2a1e] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {wishCount}
                </span>
              )}
            </Link>
            <Link to="/cart" className="flex items-center gap-2 text-[#0e2a1e] hover:opacity-70 transition-opacity relative">
              <ShoppingBag size={20} />
              <span className="text-sm hidden sm:block">Cart</span>
              {count > 0 && (
                <span className="absolute -top-2 -left-2 bg-[#a4d61e] text-[#0e2a1e] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative">
                <button onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-2 text-[#0e2a1e] hover:opacity-70 transition-opacity">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-[#0e2a1e]/15" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-[#0e2a1e] text-[#f4f2ea] flex items-center justify-center text-sm font-semibold">
                      {(user.name || user.email || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-[#0e2a1e]/10 shadow-lg py-2 z-50">
                      <div className="px-4 py-2 border-b border-[#0e2a1e]/5">
                        <p className="text-sm font-medium text-[#0e2a1e] truncate">{user.name || "My account"}</p>
                        <p className="text-xs text-[#0e2a1e]/50 truncate">{user.email}</p>
                      </div>
                      <Link to="/account" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#0e2a1e] hover:bg-[#f4f2ea]">
                        <Package size={16} /> My orders
                      </Link>
                      <Link to="/account" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#0e2a1e] hover:bg-[#f4f2ea]">
                        <Heart size={16} /> Wishlist
                      </Link>
                      {user.is_admin && (
                        <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#0e2a1e] hover:bg-[#f4f2ea]">
                          <User size={16} /> Admin panel
                        </Link>
                      )}
                      <button onClick={doLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-[#f4f2ea]">
                        <LogOut size={16} /> Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link to="/login" className="flex items-center gap-1.5 text-[#0e2a1e] hover:opacity-70 transition-opacity text-sm">
                <User size={18} className="sm:hidden" />
                <span className="hidden sm:block">Login</span>
              </Link>
            )}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 pb-3 flex items-center justify-center gap-8">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/shop/${c.id}`}
              className="text-sm text-[#0e2a1e] hover:text-[#a4820e] relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#0e2a1e] after:transition-all"
            >
              {c.name}
            </Link>
          ))}
          <Link
            to="/shop"
            className="text-sm text-[#0e2a1e] hover:text-[#a4820e] relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#0e2a1e] after:transition-all"
          >
            All
          </Link>
          <Link
            to="/donate"
            className="text-sm font-medium text-[#2f6b1e] hover:text-[#a4820e] relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#2f6b1e] after:transition-all"
          >
            Donate
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
